// @ts-strict-ignore
import * as React from 'react';
import {PureComponent} from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import kebabCase from 'lodash/kebabCase';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import {Location, NavigateFunction} from 'react-router';

import {QueryContext, QueryContextManager} from 'common/query-context';
import {ToastType} from 'common/components/toast/types';
import {parseUri, replaceQueryParamToFragment} from 'common/util';
import {AppLevelSpanIds, spans} from 'common/util/tracing';
import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {DownloadMode, ProjectFeatureFlag, ReportName, SHOW_LAST_X_DAYS_FEATURE_FLAGS} from 'common/report/constants';
import {
  getDownloadModeFromLocation,
  getProjectId,
  getReportName,
  getReportUrl,
  getWorkspaceId,
  moveReport,
} from 'common/report/util';
import {currentProjectClient} from 'common/report/queries/app-api/project-details-client';
import {addToast} from 'common/components/toast-area/toast-manager';
import {isValidBookmarkURLSlug} from 'common/report/util/bookmark-urls';
import {parseJsUrl, serializeJsUrl} from 'common/report/util/url';
import {BookmarkSaveParams, BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {reporter} from 'common/report/error-reporting';
import {removeRecentItemById, searchUtils, transformBookmarkIntoRecentItem} from 'common/report/util/search/index';
import {Report} from 'common/report/insights/models/report';
import {convertRollupBookmark} from 'common/report/insights/models/convert';
import {InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {BookmarkState, cancelWorkflow, captureWorkflowTiming, Workflow} from 'common/util/workflow-timing';
import {ProdTrackingEvent, PRODUCTION_TRACKING_EVENTS} from 'common/report/insights/tracking';
import {getReportTracker} from 'common/report/report-manager/tracking';
import {ReportTracker} from 'common/report/report-tracker';
import {BookmarkStore} from 'common/report/stores/bookmarks';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {hasDataTable} from 'common/report/insights/visualization/charts/chart-display/utils';
import {DeleteEvent, ReportEditorReact, SaveEvent, UpdateEvent} from 'common/report-editor';
import {Bookmark, DisplayBookmark} from 'common/report/dashboards/types';
import {CustomEventsStore} from 'common/report/stores/custom-events';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {DashboardOrigin, InsightsUrlOverrideKeys, InsightsUrlOverrides} from 'common/report/util/bookmark-overrides';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {ReportLayoutManager} from 'common/report/contexts/report-layout';
import {BackboneStyleRoutes} from 'common/contexts/react/react-router';
import {beforeSavingBookmark} from 'common/report/report-manager/query-lifecycles';
import {QueryMode} from 'common/report/insights/util/constants';
import {ReportEditorActions} from 'common/report/contexts/report-editor-actions';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {getCardInDashboardHref, getRouteToCurrentDashboard} from 'common/report/util/boards';
import {ApiResponseError} from 'common/util/shared-client';
import {getLocation, navigate} from 'common/util/url';
import {ContentSidenavContextValue} from 'common/widgets/content-sidenav/types';
import {LocalizationStore} from 'common/i18n/store';
import {runBookmarkMigrations} from 'common/report/queries/app-api/run-bookmark-migrations';
import {NavManager} from 'common/contexts/nav-manager/types';
import {isMetricLevelDataGroupsEnabled} from 'common/report/util/feature-sampling';

import {insightsQueryStartedWorkflowComplete} from '../util/workflow';
import './index.module.styl';

type Props = {
  bookmarkStore: BookmarkStore;
  currentProject: CurrentProjectResource;
  customEventsStore: CustomEventsStore;
  i18n: LocalizationStore;
  location: Location;
  metrics: MetricsResource;
  navigate: NavigateFunction;
  queryContextManager: QueryContextManager;
  reportEditorActions: ReportEditorActions;
  reportLayoutManager: ReportLayoutManager;
  contentSidenavContext: ContentSidenavContextValue | undefined;
  navManager: NavManager;
};

type State = {
  originDashboard: DashboardOrigin;
  loading: boolean;
  isSavingBookmark: boolean;
  report: Report;
  bookmark: Bookmark<InsightsBookmarkParams>;
  bookmarkURLSlug?: string;
  lastReportQueryContext: QueryContext;
  longUrlCount: number;
  currUrlData: null | Partial<Pick<Report, keyof Report> & {originalDashboard: DashboardOrigin}>;
};

/**
 * This is a special `location` state accessable by `location.state`
 * It's only being used when we save a new bookmark and want to reload the bookmark with an API request
 * See references to CREATE_BOOKMARK_LOCATION_STATE in the codebase for more detail
 */
const CREATE_BOOKMARK_LOCATION_STATE = `CREATE_BOOKMARK_LOCATION_STATE`;

export class InsightsApp extends PureComponent<Props, State> {
  private declare tracker: ReportTracker;
  private disconnectQueue: Array<() => void> = [];
  state: State = {
    originDashboard: null,
    longUrlCount: 0,
    report: this.defaultReportState(),

    lastReportQueryContext: null,
    loading: true,
    isSavingBookmark: false,
    bookmark: null,
    currUrlData: null,
  };

  // @ts-ignore: TS7031 - Not all code paths return a value.
  loadSavedReport = async (
    reportId: string,
    {
      overrideBookmarkParams,
      urlOverrides = {},
    }: {
      overrideBookmarkParams?: InsightsBookmarkParams;
      urlOverrides?: InsightsUrlOverrides;
    } = {},
  ) => {
    if (reportId) {
      this.captureBookmarkToQueryWorkflow({
        bookmarkState:
          isEmpty(overrideBookmarkParams) && isEmpty(urlOverrides)
            ? BookmarkState.Saved
            : BookmarkState.SavedWithOverrides,
      });
    }

    if (
      this.state.bookmark &&
      this.state.bookmark.id === parseInt(reportId, 10) &&
      // when we freshly create a new bookmark from insights report, we don't want `existingReport` to be truthy
      // so there'll be a separated ajax call to load the bookmark and originDashboard
      this.props.location.state !== CREATE_BOOKMARK_LOCATION_STATE
    ) {
      const migratedOverrideParams = overrideBookmarkParams
        ? await this.migrateBookmarkParams({type: this.state.bookmark.type, params: overrideBookmarkParams})
        : undefined;

      // existing report is only true when insights is fully loaded and you select a report from the modal
      // one exception is when we newly save a bookmark to a dashboard, `this.state.bookmark` is already present
      // without knowing what the `originDashboard` is.
      let existingReport = Report.fromBookmark(this.state.bookmark);
      // When loading a report via saved bookmark, we need to apply Single Segment Transformation.
      if (this.props.currentProject.get().value?.isRollupProject()) {
        existingReport = this.convertRollupReport(existingReport);
      }

      this.loadReport(existingReport.clone(migratedOverrideParams), {urlOverrides});
    } else {
      let bookmark = null;
      this.setState({loading: true});

      const bookmarkData = null;
      try {
        bookmark = await this.fetchBookmarkById(reportId);
      } catch (error) {
        cancelWorkflow(this, Workflow.BookmarkToQuery, BookmarkState.Invalid);
        reporter.error(`Error loading bookmarkData`, `\n`, bookmarkData, `\n`, error);
      }

      this.setState({loading: false});

      if (bookmark) {
        const migratedOverrideParams = overrideBookmarkParams
          ? await this.migrateBookmarkParams({type: bookmark.type, params: overrideBookmarkParams})
          : undefined;
        const report = this.createReportFromBookmark(bookmark);
        this.loadReport(report.clone(migratedOverrideParams), {urlOverrides});
      } else {
        this.props.navigate(``, {replace: true});
      }
    }
  };

  handleIndexRoute = () => {
    this.resetQuery();
  };

  getCurrentProject = () => {
    return this.props.currentProject.get().value;
  };

  getLongUrlBookmarkParams(parsedJsurlData: Obj): InsightsBookmarkParams | undefined {
    const bookmarkParams = omit(parsedJsurlData, InsightsUrlOverrideKeys);
    const isValid = Boolean(bookmarkParams?.sections);
    return isValid ? (bookmarkParams as InsightsBookmarkParams) : undefined;
  }

  async migrateBookmarkParams({
    params,
    type,
  }: {
    params: InsightsBookmarkParams;
    type: BookmarkType;
  }): Promise<InsightsBookmarkParams | undefined> {
    try {
      const response = await runBookmarkMigrations({type, params});
      this.trackEvent(ProdTrackingEvent.BookmarkMigrationsRequest, {
        type,
        params,
        migratedParams: response.params,
        migrationError: response.params?.[`error`],
      });
      return response.params;
    } catch (err) {
      reporter.error(`Bookmark migrations request failed`, {err, type, params});
      this.trackEvent(ProdTrackingEvent.BookmarkMigrationsRequest, {
        type,
        params,
        requestError: err,
      });
      return params;
    }
  }

  async handleJSURLRoute(rawJsURL) {
    const jsUrlInfo = parseJsUrl(rawJsURL);
    const resetStateAndNavigate = () => {
      this.props.navigate(``, {replace: true});
      this.resetQuery();
      this.setState({
        report: this.defaultReportState(),
        loading: false,
      });
    };

    if (jsUrlInfo.valid) {
      if (jsUrlInfo.correctedUrl !== rawJsURL) {
        this.props.navigate(`${jsUrlInfo.correctedUrl}`, {replace: true});
      }

      const bookmarkParams = this.getLongUrlBookmarkParams(jsUrlInfo.data);
      const urlOverrides = pick(jsUrlInfo.data, InsightsUrlOverrideKeys);

      const migratedBookmarkParams = await this.migrateBookmarkParams({
        // NOTE: I think jsurl route codepath should always be an Insights report
        type: BookmarkType.Insights,
        params: bookmarkParams,
      });

      const success = this.handleURLParamsUpdate({...migratedBookmarkParams, ...urlOverrides});
      if (success) {
        this.setState((state) => ({longUrlCount: state.longUrlCount + 1}));
      } else {
        console.error(`Problem parsing report`, bookmarkParams);
        const toast = {
          report: getReportName(),
          type: ToastType.ERROR,
          message: `Error loading report from URL`,
        };
        addToast(toast);

        resetStateAndNavigate();
      }
    } else {
      resetStateAndNavigate();
    }
  }

  handleReportIdAndTitleWithPossibleJsURL = (reportId, _reportTitle, possibleJsURL) => {
    this.handleReportIdWithPossibleJsURL(reportId, possibleJsURL);
  };

  handleReportIdWithPossibleJsURL = async (reportId, possibleJsURL) => {
    const overrides = parseJsUrl(possibleJsURL);
    await this.loadSavedReport(reportId, {
      // If overrides isn't valid, then `possibleJsURL` is actually the report title
      ...(overrides.valid
        ? {
            overrideBookmarkParams: this.getLongUrlBookmarkParams(overrides.data),
            urlOverrides: pick(overrides.data, InsightsUrlOverrideKeys),
          }
        : {}),
    });
    this.setState((state) => ({longUrlCount: state.longUrlCount + 1}));
  };

  handleJsUrlOrBookmarkURLSlug = (jsurlOrBookmarkURLSlug: string) => {
    if (isValidBookmarkURLSlug(jsurlOrBookmarkURLSlug)) {
      this.captureBookmarkToQueryWorkflow({bookmarkState: BookmarkState.BookmarkUrlSlug});
      this.setState({bookmarkURLSlug: jsurlOrBookmarkURLSlug, loading: false});
    } else {
      this.captureBookmarkToQueryWorkflow({bookmarkState: BookmarkState.AdHoc});
      this.handleJSURLRoute(jsurlOrBookmarkURLSlug);
    }
  };

  handleLoadReportId = (reportId: string) => {
    this.loadSavedReport(reportId);
  };

  captureBookmarkToQueryWorkflow({bookmarkState}: {bookmarkState: BookmarkState}) {
    captureWorkflowTiming(this, {
      key: Workflow.BookmarkToQuery,
      isComplete: insightsQueryStartedWorkflowComplete,
      additionalProps: {
        bookmark: bookmarkState,
        // synchronous caching is only enabled on a sample of users to help understand the UI perf impact of synchronous caching
        currentProjectClientSynchronousCacheEnabled: !!currentProjectClient.persistence,
        spaNavCount: this.props.navManager.getSpaNavCount(),
        isFastedit: true,
      },
    });
  }
  // JSURL handlers
  handleURLParamsUpdate(params: InsightsBookmarkParams & InsightsUrlOverrides): boolean {
    // Make sure current project store is loaded before we parse the URL
    // pass down custom event sync function so clauses can get the latest version of each custom event
    const defaultParams = this.defaultReportState().serialize();
    const displayOptions = {...defaultParams.displayOptions, ...params.displayOptions};
    let report = Report.deserialize({...defaultParams, ...params, displayOptions});
    if (report && report.valid) {
      if (this.props.currentProject.get().value?.isRollupProject()) {
        report = this.convertRollupReport(report);
      }

      // track legend.idxSelections in URL so we can eventually remove it in favor of bookmark-persisted segments
      if (params.legend && `idxSelections` in params.legend) {
        this.trackEvent(ProdTrackingEvent.DeprecatedUrlLoaded);
      }

      this.loadReport(report, {
        urlOverrides: params.originDashboard ? {originDashboard: params.originDashboard} : {},
      });
      return true;
    }
    cancelWorkflow(this, Workflow.BookmarkToQuery, BookmarkState.Invalid);
    return false;
  }

  resetQuery() {
    return this.loadReport(null);
  }

  // no access to events, but has access to people
  defaultQueryPeople() {
    const {isBlockedEvents = false, isBlockedPeople = false} = this.props.currentProject.get().value?.blocking || {};
    const eventsIntegrated = this.props.currentProject.get().value?.hasIntegratedArb;
    const peopleIntegrated = this.props.currentProject.get().value?.hasIntegratedEngage;
    return (isBlockedEvents || !eventsIntegrated) && peopleIntegrated && !isBlockedPeople;
  }

  defaultReportState(dataset = null, usePeopleMode = false, dataGroupId = null): Report {
    const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag) =>
      this.props.currentProject.get().value?.hasFeatureFlag(flag),
    );

    return Report.createDefaultReport({
      dataGroupId,
      dataset,
      queryMode:
        usePeopleMode || this.defaultQueryPeople()
          ? QueryMode.Groups
          : this.isInFunnelsApp()
            ? QueryMode.Funnels
            : this.isInRetentionApp()
              ? QueryMode.Retention
              : QueryMode.Events,
      showLastXDays: showLastXDaysFlag ? showLastXDaysFlag[0] : null,
      defaultToConversionSession: [
        ProjectFeatureFlag.FunnelsMathSessions,
        ProjectFeatureFlag.FunnelsConversionSessions,
      ].some((ff) => this.props.currentProject.get().value?.hasFeatureFlag(ff)),
      defaultToMathSession: this.props.currentProject
        .get()
        .value?.hasFeatureFlag(ProjectFeatureFlag.FunnelsMathSessions),
      metricLevelDataGroups: isMetricLevelDataGroupsEnabled(this.props.currentProject.get().value),
    });
  }

  convertRollupReport(report: Report) {
    const converted = report.clone(convertRollupBookmark(report.serialize()));
    reporter.info(`Converted insights rollup bookmark`, {report: converted});
    return converted;
  }

  componentDidMount() {
    const {queryKey} = parseUri(getLocation().href);

    if (queryKey[`redirect`]) {
      replaceQueryParamToFragment({queryKey: `redirect`});
    }

    this.props.currentProject.subscribe(this.currentProjectCallback);
    this.props.customEventsStore.fetch({projectId: getProjectId(), workspaceId: getWorkspaceId()});

    this.tracker = getReportTracker({
      appName: ReportName.Insights,
      productionEvents: PRODUCTION_TRACKING_EVENTS,
      productionPrefix: `Insights`,
      appPersistentProperties: () => ({
        [commonTrackingEventPropertyName.reportName]: ReportName.Insights,
      }),
    });

    // we heavily rely on this setState to set queryContext correctly
    // we can't use props directly in `render` because it will trigger an instance `handleUpdate` call
    // but the core-app component is not mounted, so state & navigation is not ready yet
    // so we have one level of redirection in `componentDidMount` for this `setState`
    // to make sure reportContext is only passed to report-editor after the core app is mounted
    // TODO: remove this and support for `lastReportQueryContext` in report-editor once report-editor is converted to react
    // query-context should be read from context in `report-editor#componentDidMount`
    if (this.props.queryContextManager.isDirty) {
      this.setState({
        lastReportQueryContext: this.props.queryContextManager.query,
        loading: false,
        originDashboard: this.props.queryContextManager.query.originDashboard,
      });
    }

    const downloadMode = getDownloadModeFromLocation();
    if (downloadMode === DownloadMode.None) {
      this.setState((state) => {
        const dashboardId = state.originDashboard?.id;
        // Infrequently contentSidenavContext is undefined, likely related to this code is async. Not sure exactly
        // why it occurs, but it could be because this component is unmounted, and maybe this causes this.props
        // to be undefined.
        this.props.contentSidenavContext?.setSidenavState({
          ...(dashboardId && {selectedNavItem: dashboardId.toString()}),
        });
      });
    }

    spans.getOrCreateAppLevelSpan(AppLevelSpanIds.INSIGHTS);
    window.addEventListener(`beforeunload`, () => {
      spans.endAppLevelSpan();
    });
  }

  componentWillUnmount() {
    this.disconnectQueue.forEach((disconnectAction) => disconnectAction());
    this.props.currentProject.unsubscribe(this.currentProjectCallback);
    spans.endSpanById(AppLevelSpanIds.INSIGHTS);
    window.removeEventListener(`beforeunload`, () => {
      spans.endAppLevelSpan();
    });
  }

  onDisconnected = (disconnectAction: () => void) => {
    this.disconnectQueue.push(disconnectAction);
  };

  createReportFromBookmark(bookmarkData) {
    return Report.fromBookmark(bookmarkData, this.defaultReportState());
  }

  async fetchBookmarkById(reportId): Promise<Bookmark<InsightsBookmarkParams>> {
    try {
      const bookmark = await this.props.bookmarkStore.getOrFetch<InsightsBookmarkParams>(reportId);
      this.setState({bookmark});
      this.props.contentSidenavContext?.updateSidenavState({
        selectedNavItem: bookmark.dashboard.id.toString(),
      });
      return bookmark;
    } catch (err) {
      console.error(err);
      const toast = {
        report: getReportName(),
        type: ToastType.ERROR,
        message: `Bookmark not found`,
      };
      addToast(toast);
      return null;
    }
  }

  async refreshBookmarkById(reportId): Promise<Report> {
    try {
      const bookmark = await this.props.bookmarkStore.fetchFresh<InsightsBookmarkParams>(reportId);
      this.setState({bookmark});
      return this.createReportFromBookmark(bookmark);
    } catch (err) {
      console.error(err);
      const toast = {
        report: getReportName(),
        type: ToastType.ERROR,
        message: `Bookmark not found`,
      };
      addToast(toast);
      return null;
    }
  }

  urlForReportId({reportID, bookmark}) {
    let title = bookmark?.name;
    title = title ? kebabCase(title) : ``;
    return `report/${reportID}/${title}`;
  }

  deleteBookmark(bookmark: Bookmark<InsightsBookmarkParams>) {
    const report = Report.fromBookmark(bookmark);
    const reportTrackingData = report.toTrackingData({
      i18n: this.props.i18n,
      id: bookmark.id,
      currentProjectResource: this.props.currentProject,
    });
    const bookmarkDeletionPromise = dashboardsClient.removeDashboardBookmark({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
      bookmark,
    });

    bookmarkDeletionPromise
      .then(() => {
        this.setState({bookmark: null});
        removeRecentItemById(bookmark.id);
        const dashboardLink = getReportUrl(`boards`, {
          projectId: getProjectId(),
          workspaceId: getWorkspaceId(),
        });
        navigate(dashboardLink);
      })
      .catch((err) => {
        console.error(`Error deleting: ${err}`);
        reportTrackingData[`error`] = err;
      });
  }

  // TODO opslead
  loadReport(report: Report, {urlOverrides = {}}: {urlOverrides?: InsightsUrlOverrides} = {}) {
    if (!report) {
      const defaultReport = this.defaultReportState();
      this.setState({
        report: defaultReport,
        loading: false,
      });

      this.props.reportLayoutManager.resetReportLayoutIfNeeded({
        hasDataTable: hasDataTable(defaultReport.displayOptions.chartType),
      });
      return defaultReport;
    }

    // a cloned report to stage changes in to avoid modifying the original report before committing state updates
    const stagedReport: Report = report.clone();

    const shouldApplyOverrides = !isEmpty(urlOverrides);
    if (shouldApplyOverrides) {
      const {
        additionalFilters,
        chartTypeOverride,
        dateRangeOverride,
        description,
        erfTreatments,
        originDashboard,
        querySamplingEnabled,
        reportSectionsOverride,
        timeUnitOverride,
        title,
      } = urlOverrides;
      if (additionalFilters && additionalFilters.length > 0) {
        if (stagedReport.isFilterDeterminerAll()) {
          stagedReport.appendAdditionalFilters(additionalFilters);
        } else {
          addToast({
            message: `Board filters cannot be applied to this report.`,
            type: ToastType.ERROR,
          });
        }
      }
      if (chartTypeOverride) {
        stagedReport.overrideChartType(chartTypeOverride);
      }
      if (dateRangeOverride) {
        stagedReport.overrideTimeSection(dateRangeOverride, timeUnitOverride);
      }
      if (reportSectionsOverride) {
        stagedReport.overrideReportSections(reportSectionsOverride);
      }
      if (erfTreatments) {
        stagedReport.applyErfTreatments(erfTreatments);
      }
      if (originDashboard) {
        this.setState({originDashboard});
      }

      if (title) {
        stagedReport.title = title;
      }

      if (description) {
        stagedReport.description = description;
      }

      if (!isNil(querySamplingEnabled)) {
        stagedReport.querySamplingEnabled = querySamplingEnabled;
      }

      this.updateReport({report: stagedReport, bookmarkURLSlug: null});
    }

    this.props.reportLayoutManager.resetReportLayoutIfNeeded({
      hasDataTable: hasDataTable(stagedReport.displayOptions.chartType),
    });

    this.setState({
      loading: false,
      report: stagedReport,
    });

    return report;
  }

  saveReport({
    editedBookmark,
    originalBookmark,
    dashboard,
    originDashboard,
    saveAsNew,
    trackingProps,
  }: SaveEvent<InsightsBookmarkParams>[`detail`]) {
    const bookmarkId = saveAsNew ? null : originalBookmark.id;
    const {bookmarkStore} = this.props;
    function makeBookmark(_projectId, _workspaceId, shouldSaveAsNew, bookmarkFields) {
      // modify `group` param in value comparison mode
      const jsonParams = JSON.parse(bookmarkFields.params);
      if (shouldSaveAsNew) {
        //shayan
        return dashboardsClient.addNewDashboardBookmark({
          ...(originDashboard ? {dashboardOrigin: originDashboard} : {}),
          dashboardId: bookmarkFields.dashboard_id,
          bookmarkData: bookmarkFields,
          projectId: _projectId,
          workspaceId: _workspaceId,
        });
      }
      return bookmarkStore.createOrUpdate(
        {
          ...bookmarkFields,
          params: jsonParams,
        },
        {saveAsNew: shouldSaveAsNew},
      );
    }
    const projectId = getProjectId();
    const workspaceId = getWorkspaceId();

    const {name, description, params} = editedBookmark;
    const bookmarkFields: BookmarkSaveParams = {
      type: this.getBookmarkType(),
      id: bookmarkId,
      name,
      description,
      dashboard_id: dashboard.id,
      // bookmark.params - json blob defining query parameters of bookmark
      params: JSON.stringify(beforeSavingBookmark({params, type: BookmarkType.Insights})),
    };

    this.setState({isSavingBookmark: true});
    return makeBookmark(projectId, workspaceId, saveAsNew, bookmarkFields)
      .then((bookmark: Bookmark<InsightsBookmarkParams>) => {
        const report = Report.fromBookmark(bookmark);
        this.updateReport({
          report,
          bookmarkURLSlug: null,
          updateUrl: false,
        });
        this.setState({bookmark});

        this.addReportToRecentsSearchList(bookmark);
        this.props.navigate(this.urlForReportId({reportID: bookmark.id, bookmark}), {
          // The state here enforces insight app to load a bookmark from the server
          // to get originDashboard
          state: saveAsNew ? CREATE_BOOKMARK_LOCATION_STATE : null,
        });

        if (saveAsNew && originalBookmark) {
          // we are about to navigate, so defer opening the title editor
          this.props.reportEditorActions.openTitleEditor(true);
        }

        this.props.contentSidenavContext?.updateSidenavState({
          selectedNavItem: dashboard.id.toString(),
        });

        Object.assign(trackingProps, {
          'new report': !this.state.bookmark,
          'report title': report.title,
          'report id': bookmark.id,
        });
        return bookmark;
      })
      .catch((err) => {
        console.error(`Error saving: ${err}`);
        trackingProps.error = err;
        throw err;
      })
      .then((val) => {
        this.setState({isSavingBookmark: false});
        if (trackingProps.error) {
          return Promise.reject(new Error(trackingProps.error));
        }
        return val;
      });
  }

  addReportToRecentsSearchList(bookmark: Bookmark<InsightsBookmarkParams>) {
    searchUtils.addRecentItem(
      transformBookmarkIntoRecentItem(bookmark, `insights`, {
        projectId: getProjectId(),
        userId: this.getUserId(),
        workspaceId: getWorkspaceId(),
      }),
    );
  }

  trackEvent(eventName, properties?: {[key: string]: any}) {
    this.tracker.track(eventName, properties);
  }

  getUserId() {
    return userCookieUtils.getUserIdentityFromCookie()?.id;
  }

  updateReport({
    report,
    bookmarkURLSlug,
    updateUrl = true,
  }: {
    report: Report;
    bookmarkURLSlug: string;
    updateUrl?: boolean;
  }): void {
    this.setState({report, bookmarkURLSlug});
    if (!updateUrl) {
      return;
    }

    // Persists any report updates to the url and bookmark.
    let fragment = ``;
    if (bookmarkURLSlug) {
      fragment = bookmarkURLSlug;
    } else {
      const {currUrlData} = this.state;
      const reportData = report.toUrlData();
      let newUrlData = {};

      if (!isEqual(currUrlData, reportData)) {
        newUrlData = reportData;
      }

      if (!isEmpty(newUrlData)) {
        this.setState({currUrlData: newUrlData});

        fragment = this.state.bookmark
          ? `${this.urlForReportId({
              reportID: this.state.bookmark?.id,
              bookmark: this.state.bookmark,
            })}/${serializeJsUrl(newUrlData)}`
          : `${serializeJsUrl(newUrlData)}`;
      }
    }

    if (fragment) {
      this.props.navigate(fragment, {replace: true});
    }
  }

  currentProjectCallback = () => {
    // we need to convert old rollup bookmarks to the new format and requery depending on isRollupProject
    // we could have had a more robust check if we saved versions (original PR: https://github.com/mixpanel/analytics/pull/26476),
    // but rollup project are slowly dying with plans to be replaced, so for now we can just potentially query twice on page load.
    if (this.props.currentProject.get().value?.isRollupProject()) {
      this.loadReport(this.convertRollupReport(this.state.report));
    }
  };

  isInFunnelsApp() {
    return this.props.location.pathname.includes(`app/funnels`);
  }

  isInRetentionApp() {
    return this.props.location.pathname.includes(`app/retention`);
  }

  getBookmarkType() {
    return this.isInFunnelsApp()
      ? BookmarkType.Funnels
      : this.isInRetentionApp()
        ? BookmarkType.Retention
        : BookmarkType.Insights;
  }

  getDisplayBookmark = (): DisplayBookmark<InsightsBookmarkParams> => {
    if (this.state.loading) {
      return null;
    }
    return {
      ...this.state.report?.toBookmark(),
      type: this.getBookmarkType(),
    };
  };

  handleReportEditorUpdate = (ev: UpdateEvent) => {
    const {bookmark: updatedBookmark, bookmarkURLSlug} = ev.detail;
    // need to manually add the dashboard since we expect it to be on the Report model
    const bookmark = {
      ...this.state.report.toBookmark(),
      dashboard: this.state.bookmark?.dashboard,
      ...updatedBookmark,
    };
    const report = this.createReportFromBookmark(bookmark);
    report.querySamplingEnabled = this.state.report.querySamplingEnabled;

    this.updateReport({report, bookmarkURLSlug});
  };

  handleReportEditorSave = (ev: SaveEvent) => {
    this.saveReport(ev.detail);
  };

  handleReportEditorDelete = (ev: DeleteEvent) => {
    this.deleteBookmark(ev.detail.originalBookmark);
  };

  handleReportEditorQuerySampling = (ev) => {
    const report = this.state.report;
    report.querySamplingEnabled = ev.detail;
    this.updateReport({report, bookmarkURLSlug: null});
  };

  handleReportEditorMove = async (ev: CustomEvent) => {
    try {
      const dashboard = await moveReport(this.state.bookmark?.dashboard.id, getProjectId(), ev, dashboardsClient);

      this.setState({originDashboard: dashboard});

      this.setState({loading: true});
      await this.refreshBookmarkById(this.state.bookmark.id);
      this.setState({loading: false});

      this.props.contentSidenavContext?.updateSidenavState({
        selectedNavItem: dashboard.id.toString(),
      });

      const visitBoard = getCardInDashboardHref({
        dashboardId: dashboard?.id,
        projectId: Number(getProjectId()),
        workspaceId: Number(getWorkspaceId()),
      });
      const toast = {
        cta: `Visit Board`,
        message: `Board has been moved.`,
        type: ToastType.SUCCESS,
        undoable: false,
        ctaHref: visitBoard,
      };
      addToast(toast);
    } catch (err) {
      let message = `Error moving board`;

      if (err instanceof ApiResponseError) {
        message = err.message;
      }

      reporter.error(err as Error, message);
      const toast = {
        message,
        type: ToastType.ERROR,
        timeoutMS: 20000,
      };
      addToast(toast);
    }
  };

  render() {
    const renderReportEditor = () => {
      return (
        <ReportEditorReact
          className={`inherit-size`}
          originDashboard={this.state.originDashboard}
          loading={this.state.loading || !this.state.report}
          isSavingBookmark={this.state.isSavingBookmark}
          elref={`reportEditor`}
          appMode
          enableQuerySampling={this.state.report?.querySamplingEnabled}
          editorParams={{
            defaultBookmarkType: this.getBookmarkType(),
            originalBookmark: this.state.bookmark ?? null,
            editedBookmark: this.getDisplayBookmark(),
            resultsPromise: null,
            dashboardOverrides: null,
            bookmarkURLSlug: this.state.bookmarkURLSlug,
            lastReportQueryContext: this.state.lastReportQueryContext,
            longUrlCount: this.state.longUrlCount,
          }}
          dashboardsRoute={getRouteToCurrentDashboard({
            dashboard: this.state.bookmark?.dashboard,
            includeCurrentDashboard: true,
          })}
          onSave={this.handleReportEditorSave}
          onMove={this.handleReportEditorMove}
          onUpdate={this.handleReportEditorUpdate}
          onDelete={this.handleReportEditorDelete}
          onToggleQuerySampling={this.handleReportEditorQuerySampling}
        />
      );
    };

    return (
      <>
        <BackboneStyleRoutes
          routes={{
            'segmentation-report/:reportId': this.handleLoadReportId,
            'segmentation-report/:reportId/:possibleJsURL': this.handleReportIdWithPossibleJsURL,
            'report/:reportId': this.handleLoadReportId,
            'report/:reportId/:reportTitle/:possibleJsURL': this.handleReportIdAndTitleWithPossibleJsURL,
            'report/:reportId/:possibleJsURL': this.handleReportIdWithPossibleJsURL,
            'view/:reportId': this.handleLoadReportId,
            'view/:reportId/:reportTitle/:possibleJsURL': this.handleReportIdAndTitleWithPossibleJsURL,
            'view/:reportId/:possibleJsURL': this.handleReportIdWithPossibleJsURL,
            ':jsurlOrBookmarkURLSlug': this.handleJsUrlOrBookmarkURLSlug,
            '': this.handleIndexRoute,
          }}
        />
        {renderReportEditor()}
      </>
    );
  }
}
